//
// Copyright 2025 DXOS.org
//

import { type Registry } from '@effect-atom/atom-react';
import type * as Types from 'effect/Types';

import { Filter, type JsonSchema, Obj, Order, Query, type QueryAST, Ref, Type, type View } from '@dxos/echo';
import {
  ProjectionModel,
  type SchemaPropertyDefinition,
  ViewModel,
  createEchoChangeCallback,
  getSchemaFromJsonSchema,
  getSchemaFromPropertyDefinitions,
} from '@dxos/schema';

import { Table } from '../types';

// TODO(ZaymonFC): Upstream these extra fields to SchemaPropertyDefinition to enhance schema-tools schema creation.
type PropertyDisplayProps = {
  size: number;
  title: string;
  sort: QueryAST.OrderDirection;
};

export type TablePropertyDefinition = SchemaPropertyDefinition & Partial<PropertyDisplayProps>;

/**
 * @deprecated
 */
// TODO(burdon): Remove variance.
export const getBaseSchema = ({
  type,
  typename,
  properties,
  jsonSchema,
}: {
  type?: Type.AnyEntity;
  typename?: string;
  properties?: TablePropertyDefinition[];
  jsonSchema?: Types.DeepMutable<JsonSchema.JsonSchema>;
}): { typename: string; type: Type.AnyEntity } => {
  const resolved =
    typename && properties
      ? getSchemaFromPropertyDefinitions(typename, properties)
      : (type ?? (jsonSchema ? getSchemaFromJsonSchema(jsonSchema, typename) : undefined));
  if (!resolved) {
    throw new Error('invalid properties');
  }

  return { typename: Type.getTypename(resolved)!, type: resolved };
};

export const makeDynamicTable = ({
  registry,
  type,
  properties,
}: {
  registry: Registry.Registry;
  type: Type.AnyEntity;
  properties?: TablePropertyDefinition[];
}): { projection: ProjectionModel; object: Table.Table } => {
  const jsonSchema = type.jsonSchema;
  const view = ViewModel.make({
    query: Query.select(Filter.everything()),
    jsonSchema,
    type,
    ...(properties && { fields: properties.map((property) => property.name) }),
  });
  const object = Obj.make(Table.Table, { view: Ref.make(view), sizes: {} });

  const projection = new ProjectionModel({
    registry,
    view,
    baseSchema: jsonSchema,
    change: createEchoChangeCallback(view, type),
  });
  projection.normalizeView();
  if (properties && projection.getFields()) {
    setProperties(view, projection, object, properties);
  }

  return { projection, object };
};

const setProperties = (
  view: View.View,
  projection: ProjectionModel,
  table: Table.Table,
  properties: TablePropertyDefinition[],
) => {
  for (const property of properties) {
    const field = projection.getFields().find((field) => field.path === property.name);
    if (field) {
      if (property.size !== undefined) {
        Obj.update(table, (table) => {
          table.sizes[field.path] = property.size!;
        });
      }

      if (property.title !== undefined) {
        const fieldProjection = projection.getFieldProjection(field.id);
        projection.setFieldProjection({
          ...fieldProjection,
          props: { ...fieldProjection.props, title: property.title },
        });
      }

      if (property.sort) {
        // Apply sort to query instead of deprecated view.sort
        const currentQuery = Query.fromAst(Obj.getSnapshot(view).query.ast);
        // Use any type parameter since we're working with dynamic field paths
        const newQuery = currentQuery.orderBy(Order.property<any>(field.path as string, property.sort));
        Obj.update(view, (view) => {
          // Type assertion needed because Query AST types have some variance issues.
          view.query.ast = newQuery.ast as typeof view.query.ast;
        });
      }
    }
  }
};
